/**
 * Phase 4 — Executive / Owner Dashboard.
 * UI 100% Bahasa Indonesia. Reuses shared chart components dari Phase 1-3.
 * Per-widget error isolation (satu section gagal fetch tidak membuat seluruh page blank).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { formatRupiah } from "@/lib/format";
import MobileExecutiveAlertBanner from "@/components/executive/MobileExecutiveAlertBanner";
import { RencanaManajemenWidget } from "@/components/executive/ManagementActionsPanel";
import {
  BarCard, HorizontalBarCard, LineCard, DonutCard, COLORS as CHART_COLORS,
} from "@/components/charts/DashboardCharts";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Info, ArrowRight, RefreshCw,
  FileDown, Target, Settings,
} from "lucide-react";

function pct(v) {
  if (v == null || isNaN(v)) return "-";
  return `${Number(v).toFixed(1)}%`;
}

function Trend({ delta, invert = false }) {
  // invert=true untuk metric yang "turun = positif" (misal overdue, hutang)
  if (delta == null || delta === 0 || isNaN(delta)) {
    return <span className="inline-flex items-center text-xs text-slate-500"><Minus size={12} className="mr-0.5" />Datar</span>;
  }
  const positive = (delta > 0) !== invert;
  return (
    <span className={`inline-flex items-center text-xs ${positive ? "text-emerald-600" : "text-rose-600"}`}>
      {positive ? <TrendingUp size={12} className="mr-0.5" /> : <TrendingDown size={12} className="mr-0.5" />}
      {pct(Math.abs(delta))}
    </span>
  );
}

/** Semantic KPI card. Tone diturunkan dari `tone` + nilai, bukan sekadar naik/turun. */
function Kpi({ label, value, sub, tone = "slate", testid, drillTo, trend, restricted }) {
  const toneMap = {
    emerald: "border-emerald-200",
    sky: "border-sky-200",
    amber: "border-amber-200",
    rose: "border-rose-200",
    slate: "border-slate-200",
  };
  const Wrap = drillTo ? Link : "div";
  const wrapProps = drillTo ? { to: drillTo } : {};
  return (
    <Wrap {...wrapProps} className="block">
      <Card data-testid={testid} className={`${toneMap[tone] || toneMap.slate} ${drillTo ? "hover:shadow-md hover:border-slate-400 transition cursor-pointer" : ""}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            {restricted ? <Badge variant="outline" className="text-[10px]">Terbatas</Badge> : trend}
          </div>
          <div className="text-lg md:text-xl font-bold text-slate-900 mt-1 font-mono-num">
            {restricted ? "— — —" : value}
          </div>
          {sub && <div className="text-[11px] text-slate-500 mt-1">{restricted ? "Akses dibatasi" : sub}</div>}
        </CardContent>
      </Card>
    </Wrap>
  );
}

function SectionError({ msg }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-800 text-xs">
      Gagal memuat data: {msg}
    </div>
  );
}

const SEVERITY_STYLE = {
  WARNING: "border-amber-300 bg-amber-50 text-amber-900",
  INFO: "border-sky-300 bg-sky-50 text-sky-900",
  CRITICAL: "border-rose-300 bg-rose-50 text-rose-900",
};
function AlertPanel({ alerts, onAck, canAck }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-emerald-900 text-sm" data-testid="exec-alerts-ok">
        Tidak ada peringatan yang memerlukan tindakan segera.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="exec-alerts-list">
      {alerts.map((a, i) => {
        const sev = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.INFO;
        const key = a.id || `${a.alert_code}-${i}`;
        return (
          <div key={key} className={`rounded-md border p-3 text-sm ${sev}`} data-testid={`exec-alert-${a.alert_code || a.code}`}>
            <div className="flex items-start gap-2">
              {a.severity === "INFO" ? <Info size={16} className="mt-0.5" /> : <AlertTriangle size={16} className="mt-0.5" />}
              <div className="flex-1">
                <div className="font-semibold text-xs uppercase">
                  {a.severity} · {a.category}
                  {a.acknowledged_at && <span className="ml-2 inline-flex items-center text-emerald-700 normal-case font-normal">· Diakui {new Date(a.acknowledged_at).toLocaleString("id-ID")}</span>}
                </div>
                <div>{a.message}</div>
                {(a.notes && a.notes.length > 0) && (
                  <ul className="mt-1 text-xs text-slate-700 list-disc list-inside">
                    {a.notes.slice(-3).map((n, k) => (
                      <li key={k}>{n.text} <span className="text-slate-500">— {n.by}, {new Date(n.at).toLocaleDateString("id-ID")}</span></li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                {canAck && !a.acknowledged_at && a.id && (
                  <button
                    onClick={() => onAck?.(a)}
                    className="text-xs px-2 py-0.5 rounded border bg-white hover:bg-slate-100"
                    data-testid={`exec-ack-${a.alert_code}`}>
                    Akui
                  </button>
                )}
                {a.drill_to && (
                  <Link to={a.drill_to} className="text-xs inline-flex items-center hover:underline">
                    Buka <ArrowRight size={12} className="ml-1" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [tvsa, setTvsa] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [trend, setTrend] = useState(null);
  const [watch, setWatch] = useState({ metrics: [], valid_metrics: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const user = JSON.parse(localStorage.getItem("abs_user") || "null") || {};
  const role = user.role;
  const canAck = ["super_admin", "direktur_utama", "direktur_operasional", "komisaris", "auditor"].includes(role);
  const canExport = ["super_admin", "direktur_utama", "direktur_operasional", "auditor"].includes(role);
  const canConfig = ["super_admin", "direktur_utama"].includes(role);
  const canTarget = ["super_admin", "direktur_utama", "direktur_operasional"].includes(role);

  const load = () => {
    setLoading(true); setErr(null);
    Promise.all([
      api.get(`/executive/dashboard?year=${year}&month=${month}`).catch((e) => ({ error: e })),
      api.get(`/executive/target-vs-actual?year=${year}&month=${month}`).catch(() => ({ data: null })),
      api.get(`/executive/forecast?year=${year}`).catch(() => ({ data: null })),
      api.get(`/executive/trend-comparison?year=${year}&month=${month}`).catch(() => ({ data: null })),
      api.get(`/executive/watchlist`).catch(() => ({ data: null })),
    ]).then(([d, t, f, tr, w]) => {
      if (d?.error) { setErr(formatApiError(d.error)); return; }
      setData(d?.data); setGeneratedAt(d?.data?.generated_at);
      setTvsa(t?.data || null);
      setForecast(f?.data || null);
      setTrend(tr?.data || null);
      if (w?.data) setWatch({ metrics: w.data.metrics || [], valid_metrics: w.data.valid_metrics || [] });
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const ackAlert = async (alert) => {
    const note = window.prompt("Catatan tindak lanjut (opsional):", "");
    try {
      await api.post(`/executive/alerts/${alert.id}/acknowledge`,
                       note ? { note } : {});
      toast.success("Peringatan diakui.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.post(`/executive/report?year=${year}&month=${month}`, null,
                                    { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `executive-report-${year}${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF berhasil diunduh.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleWatch = async (metric) => {
    let next = watch.metrics.includes(metric)
      ? watch.metrics.filter((m) => m !== metric)
      : [...watch.metrics, metric].slice(0, 8);
    try {
      const { data } = await api.put(`/executive/watchlist`, { metrics: next });
      setWatch((prev) => ({ ...prev, metrics: data.metrics || next }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const salesSeries = useMemo(
    () => (data?.timeseries?.sales || []).map((s) => ({ name: s.period, Omzet: s.value })),
    [data]
  );
  const cashSeries = useMemo(
    () => (data?.timeseries?.cashflow || []).map((s) => ({
      name: s.period, "Cash In": s.cash_in, "Cash Out": s.cash_out, Net: s.net_cashflow,
    })),
    [data]
  );
  const profitSeries = useMemo(() => {
    const rows = data?.timeseries?.profitability || [];
    const anyNonZero = rows.some((r) => r.revenue || r.gross_profit || r.net_profit);
    if (anyNonZero) {
      return rows.map((r) => ({ name: r.period, Revenue: r.revenue, "Gross Profit": r.gross_profit, "Net Profit": r.net_profit }));
    }
    // Fallback snapshot (jangan invent monthly dari snapshot)
    const f = data?.financial || {};
    return [
      { name: "Revenue", Nilai: f.revenue || 0 },
      { name: "HPP", Nilai: f.hpp || 0 },
      { name: "Gross Profit", Nilai: f.gross_profit || 0 },
      { name: "Opex", Nilai: f.opex || 0 },
      { name: "Net Profit", Nilai: f.net_profit || 0 },
    ];
  }, [data]);
  const profitIsSnapshot = useMemo(() => {
    const rows = data?.timeseries?.profitability || [];
    return !rows.some((r) => r.revenue || r.gross_profit || r.net_profit);
  }, [data]);

  if (err && !data) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-dashboard-page">
        <h1 className="font-display text-3xl font-bold text-slate-900">Dashboard Eksekutif</h1>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-forbidden">
          {err}
        </div>
      </div>
    );
  }

  const fin = data?.financial || {};
  const cash = data?.cash || {};
  const rec = data?.receivable || {};
  const pay = data?.payable || {};
  const sales = data?.sales || {};
  const inv = data?.inventory || {};
  const bud = data?.budget || {};
  const hr = data?.hr || {};

  // Trend hints (vs previous period) — sales growth only (yang authoritative tersedia).
  const salesTrend = sales.growth_percent != null ? (
    <Trend delta={sales.growth_percent} />
  ) : null;
  const overdueArTone = (rec.overdue_percent || 0) >= 20 ? "rose" : (rec.overdue_percent || 0) >= 10 ? "amber" : "emerald";
  const overdueApTone = (pay.overdue_percent || 0) >= 20 ? "rose" : (pay.overdue_percent || 0) >= 10 ? "amber" : "emerald";
  const netCashTone = (cash.net_cashflow || 0) < 0 ? "rose" : "emerald";

  const compositionDonut = (hr.composition || []).map((c, i) => ({
    name: c.status || "LAIN", value: c.count || 0,
    color: [CHART_COLORS.baseline, CHART_COLORS.positive, CHART_COLORS.info, CHART_COLORS.warning, CHART_COLORS.muted, CHART_COLORS.risk][i % 6],
  }));

  const arApBars = [
    { name: "Piutang", Total: rec.total_ar || 0, Terlambat: rec.overdue_ar || 0 },
    { name: "Hutang", Total: pay.total_ap || 0, Terlambat: pay.overdue_ap || 0 },
  ];

  const inventoryBars = [
    { name: "Normal", value: Math.max(0, (inv.sku_count || 0) - (inv.low_stock_count || 0) - (inv.slow_moving_count || 0) - (inv.dead_stock_count || 0)) },
    { name: "Low Stock", value: inv.low_stock_count || 0 },
    { name: "Slow Moving", value: inv.slow_moving_count || 0 },
    { name: "Dead Stock", value: inv.dead_stock_count || 0 },
  ];

  const budgetBars = bud.data_not_available ? [] : [
    { name: `${year}-${String(month).padStart(2, "0")}`, Budget: bud.budget || 0, Actual: bud.actual || 0 },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="exec-dashboard-page">
      {/* Mobile Executive Alert Banner (CRITICAL / persistent WARNING) */}
      <MobileExecutiveAlertBanner />
      {/* Rencana Manajemen widget (Phase 7 Wave 2) */}
      <RencanaManajemenWidget />
      {/* Header + Filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Dashboard Eksekutif</h1>
          <p className="text-xs md:text-sm text-slate-500">
            Ringkasan kondisi perusahaan — {year}·{String(month).padStart(2, "0")}
            {generatedAt && <> · Diperbarui {new Date(generatedAt).toLocaleString("id-ID")}</>}
            {data?.rbac?.has_hr_full === false && <> · <span className="text-amber-700">Mode terbatas (payroll disensor)</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs">Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-20 md:w-24" data-testid="exec-filter-year" />
          <Label className="text-xs">Bulan</Label>
          <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Math.max(1, Math.min(12, Number(e.target.value) || month)))} className="w-16 md:w-20" data-testid="exec-filter-month" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="exec-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          {canExport && (
            <Button variant="outline" size="sm" onClick={exportPdf} data-testid="exec-export-pdf" className="text-xs">
              <FileDown size={14} className="mr-1" />Ekspor PDF
            </Button>
          )}
          {canTarget && (
            <Link to="/executive-targets" data-testid="exec-link-targets">
              <Button variant="outline" size="sm" className="text-xs"><Target size={14} className="mr-1" />Target</Button>
            </Link>
          )}
          {canConfig && (
            <Link to="/executive-alert-settings" data-testid="exec-link-alert-config">
              <Button variant="outline" size="sm" className="text-xs"><Settings size={14} className="mr-1" />Alert Config</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Phase 6 — Pantauan Saya (watchlist) tampil paling atas jika ada */}
      {watch.metrics?.length > 0 && (
        <Card data-testid="exec-watchlist"><CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">Pantauan Saya</CardTitle>
        </CardHeader><CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {watch.metrics.map((m) => {
              const row = (trend?.metrics || []).find((r) => r.metric === m);
              if (!row) return null;
              const mom = row.mom || {};
              const value = row.unit === "percent" ? `${(row.current ?? 0).toFixed(1)}%` : formatRupiah(row.current);
              const tone = mom.semantic_positive === true ? "emerald"
                : mom.semantic_positive === false ? "rose" : "slate";
              const toneCls = { emerald: "border-emerald-200", rose: "border-rose-200", slate: "border-slate-200" }[tone];
              return (
                <Card key={m} className={toneCls} data-testid={`watch-item-${m}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-[11px] uppercase text-slate-500">{row.label}</div>
                        <div className="text-base font-bold font-mono-num">{value}</div>
                      </div>
                      <button onClick={() => toggleWatch(m)} className="text-xs text-slate-400 hover:text-rose-600" data-testid={`watch-unpin-${m}`}>unpin</button>
                    </div>
                    {mom.delta_percent != null && (
                      <div className={`text-[11px] ${mom.semantic_positive === true ? "text-emerald-600" : mom.semantic_positive === false ? "text-rose-600" : "text-slate-500"}`}>
                        MoM {mom.delta_percent > 0 ? "+" : ""}{mom.delta_percent.toFixed(1)}%
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent></Card>
      )}

      {/* Executive Alerts — tampil di atas pada mobile */}
      <div className="md:hidden">
        <AlertPanel alerts={data?.alerts || []} onAck={ackAlert} canAck={canAck} />
      </div>

      {/* Row 1: 6 KPI utama */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Omzet Bulan Ini" testid="kpi-revenue"
             value={formatRupiah(fin.revenue)}
             sub={`vs bulan lalu ${formatRupiah(sales.previous_period)}`}
             trend={salesTrend}
             drillTo="/penjualan" tone="emerald" />
        <Kpi label="Gross Profit" testid="kpi-gp"
             value={formatRupiah(fin.gross_profit)}
             sub={`HPP: ${formatRupiah(fin.hpp)}`} tone="emerald" />
        <Kpi label="Gross Margin %" testid="kpi-gm"
             value={pct(fin.gross_margin_percent)}
             sub="margin kotor" tone="sky" />
        <Kpi label="Kas & Bank" testid="kpi-cash"
             value={formatRupiah(cash.cash_and_bank)}
             sub={`Net ${year}-${String(month).padStart(2, "0")}: ${formatRupiah(cash.net_cashflow)}`}
             drillTo="/kas-bank" tone="sky" />
        <Kpi label="Total Piutang" testid="kpi-ar"
             value={formatRupiah(rec.total_ar)}
             sub={`Terlambat ${pct(rec.overdue_percent)}`}
             drillTo="/piutang" tone={overdueArTone} />
        <Kpi label="Total Hutang" testid="kpi-ap"
             value={formatRupiah(pay.total_ap)}
             sub={`Terlambat ${pct(pay.overdue_percent)}`}
             drillTo="/hutang" tone={overdueApTone} />
      </div>

      {/* Row 2: 6 KPI pelengkap */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Net Profit" testid="kpi-np"
             value={formatRupiah(fin.net_profit)}
             sub={`OpEx: ${formatRupiah(fin.opex)}`} tone="emerald" />
        <Kpi label="Net Margin %" testid="kpi-nm"
             value={pct(fin.net_margin_percent)}
             sub="margin bersih" tone="sky" />
        <Kpi label="Net Cashflow Bln" testid="kpi-netcash"
             value={formatRupiah(cash.net_cashflow)}
             sub={cash.net_cashflow < 0 ? "Arus kas negatif" : "Arus kas positif"}
             tone={netCashTone} drillTo="/kas-bank" />
        <Kpi label="Nilai Persediaan" testid="kpi-inv"
             value={formatRupiah(inv.inventory_value)}
             sub={`${inv.sku_count || 0} SKU · Dead ${inv.dead_stock_count || 0}`}
             drillTo="/persediaan" tone="slate" />
        <Kpi label="Varians Anggaran" testid="kpi-bud"
             value={bud.data_not_available ? "—" : pct(bud.variance_percent)}
             sub={bud.data_not_available ? "Belum ada budget approved" : `Budget ${formatRupiah(bud.budget)}`}
             drillTo="/tahap7d/budget/dashboard"
             tone={bud.data_not_available ? "slate" : (bud.variance_percent < -10 ? "rose" : "emerald")} />
        <Kpi label="Payroll Bulan Ini" testid="kpi-payroll"
             value={formatRupiah(hr.payroll_total)}
             sub={`${hr.active_employee || 0} karyawan aktif`}
             drillTo="/payroll/dashboard" tone="slate"
             restricted={hr.restricted} />
      </div>

      {/* Phase 5 — Target vs Realisasi + Ringkasan Manajemen + Forecast */}
      {(tvsa?.items?.length || data?.decision_summary?.length || forecast?.revenue?.available) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {tvsa?.items?.length ? (
            <Card data-testid="exec-tvsa"><CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Target size={14} /> Target vs Realisasi
              </CardTitle>
            </CardHeader><CardContent>
              <div className="space-y-2 max-h-72 overflow-auto">
                {tvsa.items.filter((r) => r.target != null).map((r) => (
                  <div key={r.metric} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1" data-testid={`exec-tvsa-${r.metric}`}>
                    <div>
                      <div className="font-semibold">{r.label}</div>
                      <div className="text-slate-500">
                        {r.unit === "percent"
                          ? `${(r.actual ?? 0).toFixed(1)}% dari ${(r.target ?? 0).toFixed(1)}%`
                          : `${formatRupiah(r.actual)} dari ${formatRupiah(r.target)}`}
                      </div>
                    </div>
                    <div className="text-right">
                      {r.achievement_percent != null ? (
                        <div className={`font-bold ${r.status === "ACHIEVED" ? "text-emerald-600" : r.status === "OFF_TRACK" ? "text-rose-600" : r.status === "AT_RISK" ? "text-amber-600" : "text-slate-700"}`}>
                          {r.achievement_percent.toFixed(0)}%
                        </div>
                      ) : (
                        <div className="font-semibold text-slate-700">
                          {r.unit === "percent" ? `${(r.gap ?? 0).toFixed(1)}` : formatRupiah(r.gap)}
                        </div>
                      )}
                      <div className="text-[10px] uppercase text-slate-500">{r.status}</div>
                    </div>
                  </div>
                ))}
                {tvsa.items.every((r) => r.target == null) && (
                  <div className="text-xs text-slate-400 italic">Belum ada target yang di-set untuk periode ini.</div>
                )}
              </div>
              {canTarget && (
                <Link to="/executive-targets" className="text-xs text-sky-700 hover:underline inline-flex items-center mt-2">
                  Kelola target <ArrowRight size={12} className="ml-1" />
                </Link>
              )}
            </CardContent></Card>
          ) : null}
          {data?.decision_summary?.length ? (
            <Card data-testid="exec-decision-summary"><CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Ringkasan Manajemen</CardTitle>
            </CardHeader><CardContent>
              <ul className="space-y-2 text-xs">
                {data.decision_summary.map((p, i) => (
                  <li key={i} className="flex gap-2" data-testid={`exec-ds-${p.category}`}>
                    <span className={p.severity === "WARNING" ? "text-amber-600" : "text-sky-600"}>●</span>
                    <span><span className="font-semibold uppercase">{p.category}</span> — {p.message}</span>
                  </li>
                ))}
              </ul>
            </CardContent></Card>
          ) : null}
          {forecast?.revenue?.available || forecast?.cashflow?.available ? (
            <Card data-testid="exec-forecast"><CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Forecast Periode Berikutnya</CardTitle>
            </CardHeader><CardContent>
              <div className="space-y-3 text-xs">
                {forecast?.revenue?.available && (
                  <div>
                    <div className="text-[11px] uppercase text-slate-500">Omzet (proyeksi)</div>
                    <div className="text-lg font-bold">{formatRupiah(forecast.revenue.projected_next_period)}</div>
                    <div className="text-slate-500">
                      Rolling avg 3M: {formatRupiah(forecast.revenue.rolling_avg_3m)} · R²: {forecast.revenue.linear_trend_next?.r_squared?.toFixed(2) ?? "—"}
                    </div>
                  </div>
                )}
                {forecast?.cashflow?.available && (
                  <div>
                    <div className="text-[11px] uppercase text-slate-500">Net Cashflow (proyeksi)</div>
                    <div className={`text-lg font-bold ${(forecast.cashflow.projected_next_period || 0) < 0 ? "text-rose-600" : ""}`}>
                      {formatRupiah(forecast.cashflow.projected_next_period)}
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-slate-400 italic">
                  Forecast sederhana berdasarkan {forecast?.revenue?.periods_available || forecast?.cashflow?.periods_available} periode historis (rolling avg 3M + trend linier). Bukan prediksi probabilistic.
                </div>
              </div>
            </CardContent></Card>
          ) : null}
        </div>
      )}

      {/* Phase 6 — Perbandingan Kinerja (MoM / YoY) */}
      {trend?.metrics?.length ? (
        <Card data-testid="exec-trend-comparison"><CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center justify-between">
            <span>Perbandingan Kinerja — {year}-{String(month).padStart(2, "0")} vs Bulan Lalu & Tahun Lalu</span>
            <span className="text-[10px] font-normal text-slate-500">YTD {year} vs {year - 1}</span>
          </CardTitle>
        </CardHeader><CardContent className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="exec-trend-table">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-1">Metric</th>
                <th className="py-1 text-right">Aktual</th>
                <th className="py-1 text-right">Bln Lalu</th>
                <th className="py-1 text-right">Δ MoM</th>
                <th className="py-1 text-right">Thn Lalu</th>
                <th className="py-1 text-right">Δ YoY</th>
              </tr>
            </thead>
            <tbody>
              {trend.metrics.map((r) => {
                const isPct = r.metric.endsWith("_percent");
                const fmt = (v) => isPct ? `${(v ?? 0).toFixed(1)}%` : formatRupiah(v);
                const momCls = r.mom?.semantic_positive === true ? "text-emerald-600" : r.mom?.semantic_positive === false ? "text-rose-600" : "text-slate-500";
                const yoyCls = r.yoy?.semantic_positive === true ? "text-emerald-600" : r.yoy?.semantic_positive === false ? "text-rose-600" : "text-slate-500";
                const momStr = r.mom?.delta_percent != null ? `${r.mom.delta_percent > 0 ? "+" : ""}${r.mom.delta_percent.toFixed(1)}%` : "—";
                const yoyStr = r.yoy?.delta_percent != null ? `${r.yoy.delta_percent > 0 ? "+" : ""}${r.yoy.delta_percent.toFixed(1)}%` : "—";
                const pinned = watch.metrics.includes(r.metric);
                return (
                  <tr key={r.metric} className="border-b border-slate-100" data-testid={`trend-row-${r.metric}`}>
                    <td className="py-1">
                      <button onClick={() => toggleWatch(r.metric)}
                                className={`text-[10px] mr-1 ${pinned ? "text-amber-500" : "text-slate-300"} hover:text-amber-600`}
                                title={pinned ? "Unpin dari Pantauan Saya" : "Pin ke Pantauan Saya"}
                                data-testid={`trend-pin-${r.metric}`}>
                        {pinned ? "★" : "☆"}
                      </button>
                      {r.label}
                      <span className="text-[9px] text-slate-400 ml-1">({r.direction})</span>
                    </td>
                    <td className="py-1 text-right font-semibold font-mono-num">{fmt(r.current)}</td>
                    <td className="py-1 text-right text-slate-500 font-mono-num">{fmt(r.previous_month)}</td>
                    <td className={`py-1 text-right ${momCls}`}>{momStr}</td>
                    <td className="py-1 text-right text-slate-500 font-mono-num">{fmt(r.same_month_last_year)}</td>
                    <td className={`py-1 text-right ${yoyCls}`}>{yoyStr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {trend.commentary?.length > 0 && (
            <div className="mt-3 border-t pt-2">
              <div className="text-[11px] uppercase text-slate-500 mb-1">Ringkasan Perubahan</div>
              <ul className="space-y-1 text-xs">
                {trend.commentary.map((c, i) => (
                  <li key={i} className="flex gap-2" data-testid={`trend-commentary-${i}`}>
                    <span className={c.severity === "WARNING" ? "text-amber-600" : "text-sky-600"}>●</span>
                    <span><span className="font-semibold uppercase">{c.category}</span> — {c.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent></Card>
      ) : null}

      {/* Row 3: Tren Omzet | Cashflow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data?.timeseries?.sales_error ? <SectionError msg={data.timeseries.sales_error} /> : (
          <LineCard
            title={`Tren Omzet ${year}`}
            testId="chart-exec-sales"
            hint="Belum ada data omzet untuk tahun ini."
            currency
            data={salesSeries}
            lines={[{ key: "Omzet", label: "Omzet", color: CHART_COLORS.baseline }]}
          />
        )}
        {data?.timeseries?.cashflow_error ? <SectionError msg={data.timeseries.cashflow_error} /> : (
          <LineCard
            title={`Arus Kas ${year}`}
            testId="chart-exec-cashflow"
            hint="Belum ada aktivitas kas/bank tahun ini."
            currency
            data={cashSeries}
            lines={[
              { key: "Cash In", label: "Cash In", color: CHART_COLORS.positive },
              { key: "Cash Out", label: "Cash Out", color: CHART_COLORS.risk },
              { key: "Net", label: "Net", color: CHART_COLORS.baseline },
            ]}
          />
        )}
      </div>

      {/* Row 4: Profitabilitas | AR/AP Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {profitIsSnapshot ? (
          <BarCard
            title={`Profitabilitas (snapshot ${year}-${String(month).padStart(2, "0")})`}
            testId="chart-exec-profit"
            hint="Belum ada data profitabilitas."
            currency
            data={profitSeries}
            bars={[{ key: "Nilai", label: "Nilai", color: CHART_COLORS.baseline }]}
          />
        ) : (
          <LineCard
            title={`Profitabilitas ${year}`}
            testId="chart-exec-profit"
            hint="Belum ada data profitabilitas."
            currency
            data={profitSeries}
            lines={[
              { key: "Revenue", label: "Revenue", color: CHART_COLORS.baseline },
              { key: "Gross Profit", label: "Gross Profit", color: CHART_COLORS.positive },
              { key: "Net Profit", label: "Net Profit", color: CHART_COLORS.info },
            ]}
          />
        )}
        <BarCard
          title="Kesehatan Piutang & Hutang"
          testId="chart-exec-arap"
          hint="Belum ada data AR/AP terbuka."
          currency
          data={arApBars}
          bars={[
            { key: "Total", label: "Total", color: CHART_COLORS.baseline },
            { key: "Terlambat", label: "Terlambat", color: CHART_COLORS.risk },
          ]}
        />
      </div>

      {/* Row 5: Budget vs Actual | Inventory Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {bud.data_not_available ? (
          <Card data-testid="chart-exec-budget-empty"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Anggaran vs Realisasi</CardTitle></CardHeader>
            <CardContent><div className="h-56 flex items-center justify-center text-sm text-slate-400 italic text-center px-4">
              Belum ada budget approved/locked untuk periode ini.
            </div></CardContent>
          </Card>
        ) : (
          <BarCard
            title={`Anggaran vs Realisasi ${year}-${String(month).padStart(2, "0")}`}
            testId="chart-exec-budget"
            hint="Belum ada budget approved."
            currency
            data={budgetBars}
            bars={[
              { key: "Budget", label: "Budget", color: CHART_COLORS.baseline },
              { key: "Actual", label: "Actual", color: CHART_COLORS.positive },
            ]}
          />
        )}
        <BarCard
          title="Kondisi Persediaan"
          testId="chart-exec-inventory"
          hint="Belum ada data persediaan."
          data={inventoryBars}
          bars={[{ key: "value", label: "SKU", color: CHART_COLORS.info }]}
        />
      </div>

      {/* Row 6: HR Snapshot | Executive Alerts (desktop/tablet) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="exec-hr-snapshot"><CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">HR Snapshot</CardTitle>
        </CardHeader><CardContent>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-[11px] uppercase text-slate-500">Total Karyawan</div><div className="text-xl font-bold">{hr.total_employee || 0}</div></div>
            <div><div className="text-[11px] uppercase text-slate-500">Aktif</div><div className="text-xl font-bold text-emerald-700">{hr.active_employee || 0}</div></div>
            <div><div className="text-[11px] uppercase text-slate-500">New Hire Bln Ini</div><div className="text-xl font-bold">{hr.new_hire || 0}</div></div>
            <div><div className="text-[11px] uppercase text-slate-500">Offboarding Bln Ini</div><div className="text-xl font-bold">{hr.offboarding || 0}</div></div>
          </div>
          {compositionDonut.some((c) => c.value > 0) && (
            <div className="mt-3">
              <DonutCard
                title="Komposisi Status Karyawan"
                testId="chart-exec-hr-comp"
                hint="Belum ada data komposisi."
                data={compositionDonut}
              />
            </div>
          )}
          <Link to="/hrd-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center mt-2">
            Lihat Dashboard HR lengkap <ArrowRight size={12} className="ml-1" />
          </Link>
        </CardContent></Card>
        <Card data-testid="exec-alerts-panel"><CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">Perlu Perhatian</CardTitle>
        </CardHeader><CardContent>
          <AlertPanel alerts={data?.alerts || []} onAck={ackAlert} canAck={canAck} />
        </CardContent></Card>
      </div>
    </div>
  );
}
